/**
 * Custom hook for WebSocket question parsing events
 * Provides real-time question parsing functionality with progress tracking
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import socketService, { QuestionParsingEvent } from '../services/SocketService';
import { parseQuestions, SurveyMode } from '../utils/simulationApi';

interface ParsedQuestion {
  id: string;
  text: string;
  type: string;
  choices?: string[];
  scale_min?: number;
  scale_max?: number;
  max_selections?: number;
  ranking_count?: number;
  text_limit?: number;
  required?: boolean;
  image_paths?: string[];
  image_descriptions?: string[];
}


interface QuestionParsingState {
  isConnected: boolean;
  isParsing: boolean;
  progress: number;
  currentStep: string | null;
  parsedQuestions: ParsedQuestion[];
  error: string | null;
  taskId: string | null;
  questionsCount: number;
}

interface UseQuestionParsingSocketOptions {
  onParsingStarted?: (data: QuestionParsingEvent) => void;
  onParsingProgress?: (data: QuestionParsingEvent) => void;
  onParsingCompleted?: (data: QuestionParsingEvent) => void;
  onParsingFailed?: (data: QuestionParsingEvent) => void;
  onParsingCancelled?: (data: QuestionParsingEvent) => void;
  onConnectionStatus?: (connected: boolean) => void;
  autoConnect?: boolean;
}

interface UseQuestionParsingSocketReturn {
  parsingState: QuestionParsingState;
  triggerParsing: (questions: string[], goal?: string, context?: string, questionImages?: Record<number, string[]>, advanced_parsing?: boolean, questionTypes?: Record<number, string>, surveyMode?: SurveyMode, twoPassParsing?: boolean) => Promise<string>;
  cancelParsing: (taskId: string) => Promise<void>;
  clearParsingState: () => void;
  connect: () => Promise<void>;
  disconnect: () => void;
}

const useQuestionParsingSocket = (
  options: UseQuestionParsingSocketOptions = {}
): UseQuestionParsingSocketReturn => {
  const {
    onParsingStarted,
    onParsingProgress,
    onParsingCompleted,
    onParsingFailed,
    onParsingCancelled,
    onConnectionStatus,
    autoConnect = false
  } = options;

  const [parsingState, setParsingState] = useState<QuestionParsingState>({
    isConnected: false,
    isParsing: false,
    progress: 0,
    currentStep: null,
    parsedQuestions: [],
    error: null,
    taskId: null,
    questionsCount: 0
  });

  const activeTaskIdRef = useRef<string | null>(null);
  const subscriberIdRef = useRef<string | null>(null);

  // Handle question parsing started event
  const handleParsingStarted = useCallback((data: QuestionParsingEvent) => {
    setParsingState(prev => ({
      ...prev,
      isParsing: true,
      progress: 0,
      currentStep: 'Starting question parsing...',
      error: null,
      taskId: data.task_id,
      questionsCount: data.questions_count || 0
    }));
    
    activeTaskIdRef.current = data.task_id;
    onParsingStarted?.(data);
  }, [onParsingStarted]);

  // Handle question parsing progress event
  const handleParsingProgress = useCallback((data: QuestionParsingEvent) => {
    // Only update if this is the active task
    if (data.task_id === activeTaskIdRef.current) {
      setParsingState(prev => ({
        ...prev,
        progress: data.progress || 0,
        currentStep: data.current_step || null
      }));
      
      onParsingProgress?.(data);
    }
  }, [onParsingProgress]);

  // Handle question parsing completed event
  const handleParsingCompleted = useCallback((data: QuestionParsingEvent) => {
    
    // Update state regardless of task ID for now (for debugging)
    setParsingState(prev => ({
      ...prev,
      isParsing: false,
      progress: 100,
      currentStep: 'Parsing completed',
      parsedQuestions: data.parsed_questions || [],
      error: null,
      taskId: data.task_id
    }));
    
    // Store the task ID for future reference
    activeTaskIdRef.current = data.task_id;
    onParsingCompleted?.(data);
  }, [onParsingCompleted]);

  // Handle question parsing failed event
  const handleParsingFailed = useCallback((data: QuestionParsingEvent) => {
    // Only update if this is the active task
    if (data.task_id === activeTaskIdRef.current) {
      setParsingState(prev => ({
        ...prev,
        isParsing: false,
        progress: 0,
        currentStep: null,
        error: data.error || data.detail || 'Parsing failed',
        parsedQuestions: []
      }));
      
      activeTaskIdRef.current = null;
      onParsingFailed?.(data);
    }
  }, [onParsingFailed]);

  // Handle question parsing cancelled event
  const handleParsingCancelled = useCallback((data: QuestionParsingEvent) => {
    // Only update if this is the active task
    if (data.task_id === activeTaskIdRef.current) {
      setParsingState(prev => ({
        ...prev,
        isParsing: false,
        progress: 0,
        currentStep: null,
        error: null,
        parsedQuestions: []
      }));
      
      activeTaskIdRef.current = null;
      onParsingCancelled?.(data);
    }
  }, [onParsingCancelled]);

  // Handle connection status changes
  const handleConnectionStatus = useCallback((connected: boolean) => {
    setParsingState(prev => ({
      ...prev,
      isConnected: connected
    }));
    
    onConnectionStatus?.(connected);
  }, [onConnectionStatus]);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      await socketService.connect();
      
      // Clean up any existing subscription before creating new one
      if (subscriberIdRef.current) {
        socketService.unsubscribeSubscriber(subscriberIdRef.current);
        subscriberIdRef.current = null;
      }
      
      // Subscribe to question parsing events and store subscriber ID
      const subscriberId = socketService.subscribeToEvents({
        onQuestionParsingStarted: handleParsingStarted,
        onQuestionParsingProgress: handleParsingProgress,
        onQuestionParsingCompleted: handleParsingCompleted,
        onQuestionParsingFailed: handleParsingFailed,
        onQuestionParsingCancelled: handleParsingCancelled
      });
      subscriberIdRef.current = subscriberId;
      
      setParsingState(prev => ({ ...prev, isConnected: true }));
      handleConnectionStatus(true);
      
    } catch (error) {
      console.error('❌ Failed to connect to WebSocket:', error);
      setParsingState(prev => ({ ...prev, isConnected: false }));
      handleConnectionStatus(false);
    }
  }, [
    handleParsingStarted,
    handleParsingProgress,
    handleParsingCompleted,
    handleParsingFailed,
    handleParsingCancelled,
    handleConnectionStatus
  ]);

  // Disconnect from WebSocket (only unsubscribes this hook's events, doesn't disconnect the service)
  const disconnect = useCallback(() => {
    // Only unsubscribe this specific subscriber, don't disconnect the whole service
    if (subscriberIdRef.current) {
      socketService.unsubscribeSubscriber(subscriberIdRef.current);
      subscriberIdRef.current = null;
    }
    setParsingState(prev => ({ ...prev, isConnected: false }));
    handleConnectionStatus(false);
  }, [handleConnectionStatus]);

  // Trigger question parsing
  const triggerParsing = useCallback(async (
    questions: string[],
    goal: string = '',
    context: string = '',
    questionImages?: Record<number, string[]>,
    advanced_parsing: boolean = false,
    questionTypes?: Record<number, string>,
    surveyMode: SurveyMode = 'ai_sim',
    twoPassParsing: boolean = false,
  ): Promise<string> => {
    try {
      const data = await parseQuestions(questions, goal, context, questionImages, advanced_parsing, questionTypes, surveyMode, twoPassParsing);
      
      // Store the task ID immediately when we get it from the API
      activeTaskIdRef.current = data.task_id;
      
      // Update parsing state to indicate we're parsing
      setParsingState(prev => ({
        ...prev,
        isParsing: true,
        taskId: data.task_id,
        progress: 0,
        error: null
      }));
      
      // If using polling mode, start polling for this task
      if (socketService.isPollingMode()) {
        socketService.startPollingForQuestionParsing(data.task_id);
      }
      
      return data.task_id;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to trigger parsing';
      setParsingState(prev => ({
        ...prev,
        error: errorMessage,
        isParsing: false
      }));
      throw error;
    }
  }, []);

  // Cancel question parsing
  const cancelParsing = useCallback(async (taskId: string): Promise<void> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/parse-questions/${taskId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Stop polling if active
      if (socketService.isPollingMode()) {
        socketService.stopPollingForQuestionParsing(taskId);
      }

      // Clear active task
      if (activeTaskIdRef.current === taskId) {
        activeTaskIdRef.current = null;
        setParsingState(prev => ({
          ...prev,
          isParsing: false,
          progress: 0,
          currentStep: null
        }));
      }

    } catch (error) {
      console.error('Failed to cancel parsing:', error);
      throw error;
    }
  }, []);

  // Clear parsing state
  const clearParsingState = useCallback(() => {
    // Stop polling if active
    if (activeTaskIdRef.current && socketService.isPollingMode()) {
      socketService.stopPollingForQuestionParsing(activeTaskIdRef.current);
    }
    
    setParsingState(prev => ({
      ...prev,
      isParsing: false,
      progress: 0,
      currentStep: null,
      parsedQuestions: [],
      error: null,
      taskId: null,
      questionsCount: 0
    }));
    activeTaskIdRef.current = null;
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect]); // Removed connect and disconnect from dependencies

  return {
    parsingState,
    triggerParsing,
    cancelParsing,
    clearParsingState,
    connect,
    disconnect
  };
};

export default useQuestionParsingSocket;